/**
 * Adding a vendor from its contract exhibit.
 *
 * Two steps, kept apart on purpose. The model proposes; a person confirms. Nothing is
 * stored between the two, so an extraction nobody looked at can never become a figure on
 * a dashboard — which would be the unverified assertion this product exists to catch a
 * supplier making.
 */

import { useState } from "react";
import { Notice, Section, StatTile } from "../components/Chrome";
import { ProvenanceBadge } from "../components/Badges";
import { createVendor, extractContract } from "../lib/api";
import type { ContractProposal, ExtractionResult } from "../lib/types";

type Stage = "paste" | "extracting" | "review" | "saving" | "done";

function ProposalTable({ proposal }: { proposal: ContractProposal }) {
  return (
    <div className="card table-scroll">
      <table>
        <thead>
          <tr>
            <th>Service level</th>
            <th>Target</th>
            <th style={{ textAlign: "right" }}>Weight</th>
            <th>Can we measure it</th>
            <th>From the document</th>
          </tr>
        </thead>
        <tbody>
          {proposal.service_levels.map((level) => (
            <tr key={level.id}>
              <td>
                <div className="serif" style={{ fontSize: 14.5 }}>
                  {level.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--slate)", marginTop: 3 }}>
                  {level.formula_text}
                </div>
              </td>
              <td style={{ color: "var(--slate)", whiteSpace: "nowrap" }}>
                {level.direction === "gte" ? "≥" : "≤"} {level.expected_target}
                {level.unit}
                {level.minimum_target !== null && (
                  <div style={{ fontSize: 11, opacity: 0.75 }}>
                    min {level.minimum_target}
                    {level.unit}
                  </div>
                )}
              </td>
              <td className="num" style={{ textAlign: "right", color: "var(--slate)" }}>
                {level.weight ?? (level.credit_allocation_pct !== null
                  ? `${(level.credit_allocation_pct * 100).toFixed(0)}%`
                  : "—")}
              </td>
              <td>
                <ProvenanceBadge provenance={level.provenance} />
              </td>
              <td style={{ fontSize: 12, color: "var(--slate)", maxWidth: 320 }}>
                “{level.quote}”
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OnboardingView({
  tenant,
  onAdded,
}: {
  tenant: string | null;
  onAdded: (vendorId: string) => void;
}) {
  const [stage, setStage] = useState<Stage>("paste");
  const [document, setDocument] = useState("");
  const [result, setResult] = useState<ExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runExtraction() {
    setStage("extracting");
    setError(null);
    try {
      setResult(await extractContract(document));
      setStage("review");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The extraction failed.");
      setStage("paste");
    }
  }

  async function confirm() {
    if (!result) return;
    setStage("saving");
    setError(null);
    try {
      const created = await createVendor(result.proposal, tenant, document);
      setStage("done");
      onAdded(created.vendor_id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The vendor could not be saved.");
      setStage("review");
    }
  }

  return (
    <div className="fade-in">
      <Section
        title="Add a vendor"
        subtitle="Paste the service level exhibit. What comes back is a proposal: every obligation the document states, with the words it came from, for you to check before anything is measured."
      >
        {stage === "paste" || stage === "extracting" ? (
          <>
            <textarea
              value={document}
              onChange={(event) => setDocument(event.target.value)}
              placeholder="Paste Exhibit A, Exhibit B, or the service level section of the agreement…"
              aria-label="Contract exhibit"
              rows={14}
              style={{
                width: "100%",
                background: "transparent",
                border: "1px solid var(--line-strong)",
                borderRadius: 8,
                padding: 14,
                color: "var(--parchment)",
                fontSize: 13,
                lineHeight: 1.6,
                fontFamily: "var(--sans)",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12 }}>
              <button
                onClick={() => void runExtraction()}
                disabled={document.trim().length < 40 || stage === "extracting"}
                style={{
                  background: "var(--gold)",
                  border: "none",
                  borderRadius: 8,
                  padding: "11px 20px",
                  color: "var(--ink)",
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: document.trim().length < 40 || stage === "extracting" ? 0.5 : 1,
                }}
              >
                {stage === "extracting" ? "Reading the exhibit…" : "Read the exhibit"}
              </button>
              <span style={{ fontSize: 12, color: "var(--slate)" }}>
                Nothing is saved until you confirm what comes back.
              </span>
            </div>
          </>
        ) : null}

        {error && (
          <div style={{ marginTop: 14 }}>
            <Notice tone="warn">{error}</Notice>
          </div>
        )}
      </Section>

      {result && (stage === "review" || stage === "saving" || stage === "done") && (
        <>
          <Section
            title={result.proposal.vendor_name}
            subtitle={result.proposal.tier_rationale}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 12,
              }}
            >
              <StatTile
                label="Service levels found"
                value={String(result.summary.service_levels)}
              />
              <StatTile
                label="We can measure"
                value={String(result.summary.measurable)}
                note="A source is connected for these"
                tone="gold"
              />
              <StatTile
                label="Vendor-attested"
                value={String(result.summary.attested)}
                note="No source connected yet"
              />
              <StatTile
                label="Credit model"
                value={result.proposal.credit_model}
                note={
                  result.proposal.credit_model === "A"
                    ? "Pool split by weight"
                    : "At-risk fees by allocation"
                }
              />
            </div>
          </Section>

          <Section
            title="What the document says"
            subtitle="Each row quotes the words it came from. A service level with no quote was not recorded."
          >
            <ProposalTable proposal={result.proposal} />
            <div style={{ marginTop: 12 }}>
              <Notice>
                The attested rows are not a gap in the extraction. No source is connected
                that can verify those metrics here, so the platform will report the
                vendor's figure and make no claim about it — the same rule every seeded
                vendor is held to. Connecting a source later moves them across.
              </Notice>
            </div>
          </Section>

          <Section title="" subtitle="">
            {stage === "done" ? (
              <Notice>
                {result.proposal.vendor_name} has been added and is being measured. It
                appears in the portfolio with the rest.
              </Notice>
            ) : (
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <button
                  onClick={() => void confirm()}
                  disabled={stage === "saving"}
                  style={{
                    background: "var(--gold)",
                    border: "none",
                    borderRadius: 8,
                    padding: "11px 20px",
                    color: "var(--ink)",
                    fontSize: 13,
                    fontWeight: 600,
                    opacity: stage === "saving" ? 0.5 : 1,
                  }}
                >
                  {stage === "saving" ? "Adding…" : "Add this vendor"}
                </button>
                <button
                  onClick={() => {
                    setResult(null);
                    setStage("paste");
                  }}
                  style={{
                    background: "transparent",
                    border: "1px solid var(--line-strong)",
                    borderRadius: 8,
                    padding: "11px 18px",
                    color: "var(--slate)",
                    fontSize: 13,
                  }}
                >
                  Start again
                </button>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
